import axios from "axios";
import type { AppThunk } from "@Store/index";
import { updateBlogData, updateBlogReadyStatus } from "./blogPageReducer";
import getImagePath from "@Utils/getImagePath";
import {
  getBlogFAQSchema,
  getBlogHowToSchema,
  getBlogItemListSchema,
  getBlogPostSchema,
  getBlogVideoSchema,
} from "../helper";
import { getMetaSuccess } from "@App/appReducer";
import config from "@Config/index";
import getBlogCollectionUrl from "@Pages/Blog/utils/getBlogCollectionUrl";
import type { RegionalBlogLanguage } from "@Pages/Blog/utils/getBlogCollectionUrl";
import getBlogImageUrl from "@Pages/Blog/utils/getBlogImageUrl";

interface GetBlogPageDataParams {
  slug: string;
  url?: string;
  staticContext?: any;
  isCategory?: boolean;
  isTag?: boolean;
  isSearch?: boolean;
  searchQuery?: string;
  isLatest?: boolean;
  isDraft?: boolean;
  lang?: RegionalBlogLanguage;
}

const getBlogPageData =
  ({
    slug,
    url,
    staticContext,
    isCategory = false,
    isTag = false,
    isSearch = false,
    searchQuery = "",
    isLatest = false,
    isDraft = false,
    lang,
  }: GetBlogPageDataParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateBlogReadyStatus("request"));

      let apiUrl: string;
      if (isLatest) {
        // For latest page, fetch all blog posts sorted by updatedAt with limit of 100
        apiUrl = `${getBlogCollectionUrl(
          "blog-articles",
          lang,
        )}?limit=100&sort=-updatedAt&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&where[_status][equals]=published&where[isArchived][equals]=false`;
      } else if (isCategory) {
        // For category pages, fetch only required fields
        apiUrl = `${getBlogCollectionUrl(
          "blog-articles",
          lang,
        )}?where[category.slug][equals]=${slug}&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&limit=0&where[_status][equals]=published&where[isArchived][equals]=false`;
      } else if (isTag) {
        // For tag pages, fetch only required fields including tags
        apiUrl = `${getBlogCollectionUrl(
          "blog-articles",
          lang,
        )}?where[tags.slug][equals]=${slug}&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[tags]=true&limit=0&where[_status][equals]=published&where[isArchived][equals]=false`;
      } else if (isSearch) {
        // For search pages, fetch only required fields
        apiUrl = `${getBlogCollectionUrl(
          "blog-articles",
          lang,
        )}?where[name][contains]=${encodeURIComponent(
          searchQuery,
        )}&select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&limit=0&where[_status][equals]=published&where[isArchived][equals]=false`;
      } else {
        // For single blog post, fetch all fields
        // Use draft status if isDraft is true, otherwise use published
        // const status = isDraft ? "draft" : "published";
        apiUrl = `${getBlogCollectionUrl(
          "blog-articles",
          lang,
        )}?where[slug][equals]=${slug}&where[_status][equals]=published&where[isArchived][equals]=false`;

        // Add cache-busting parameter for draft blogs
        if (isDraft) {
          apiUrl += `&no_cache_param=${Date.now()}`;
        }
      }

      const res = await axios.get(apiUrl);

      if (isCategory || isTag || isSearch || isLatest) {
        // For category/tag/search/latest pages, return the array of blog posts
        let blogData = res.data?.docs || [];
        const totalDocs = res.data?.totalDocs || 0;

        // Fetch categories in parallel for category/tag/search/latest pages
        let categories: any[] = [];
        try {
          const categoriesResponse = await axios.get(
            `${getBlogCollectionUrl(
              "categories",
              lang,
            )}?select[id]=true&select[name]=true&select[slug]=true&limit=0&where[_status][equals]=published`,
          );
          categories = categoriesResponse.data?.docs || [];
        } catch (error) {
          console.error("Error fetching categories:", error);
        }

        if (!blogData || blogData.length === 0) {
          // For search queries, allow empty results (show 0 results page instead of 404)
          if (isSearch) {
            // Set meta data for empty search results
            const meta: any = {};
            meta.title = searchQuery
              ? `No Results for "${searchQuery}" | Amber`
              : "Search Blogs and Articles | Amber";
            meta.description = searchQuery
              ? `No blog posts found for "${searchQuery}". Try searching with different keywords.`
              : "Search for blog posts and articles on Amber.";
            meta.canonical_url_suffix = searchQuery
              ? `${lang ? `/${lang}` : ""}/blog/search?query=${encodeURIComponent(searchQuery)}`
              : `${lang ? `/${lang}` : ""}/blog/search`;
            meta.keywords = [];
            meta.robots = "noindex,follow"; // Make search pages noindex,follow
            meta.excludeAlternateLinks = true;

            dispatch(getMetaSuccess(meta));
            dispatch(
              updateBlogData({
                categoryData: [],
                categories,
                slug,
                canonical_url: url,
                isCategory,
                isTag,
                isSearch,
                searchQuery,
                totalDocs: 0,
                isLatest,
              }),
            );
            dispatch(updateBlogReadyStatus("success"));
            return;
          }

          // For other types (category, tag, latest), return 404
          let pageType = "category";
          if (isLatest) {
            pageType = "latest";
          } else if (isTag) {
            pageType = "tag";
          }
          console.error(`No blog posts found for ${pageType}:`, slug);
          dispatch(updateBlogReadyStatus("failure"));
          if (staticContext) {
            staticContext.statusCode = 404;
          }
          return;
        }

        // Rewrite image URLs for China
        blogData = blogData.map((blog: any) => ({
          ...blog,
          mainImage: blog.mainImage
            ? { ...blog.mainImage, url: getBlogImageUrl(blog.mainImage.url) }
            : blog.mainImage,
        }));

        // For tags, add the matched tag info to each blog post for easy access
        if (isTag && blogData.length > 0) {
          blogData = blogData.map((blog: any) => {
            const matchedTag = blog.tags?.find((tag: any) => tag.slug === slug);
            return {
              ...blog,
              matchedTag,
            };
          });
        }

        // Set meta data for category/tag/search/latest pages
        const meta: any = {};
        meta.excludeAlternateLinks = true; // Blog pages dont need alternate links

        if (isLatest) {
          meta.title = "Latest Blogs and Articles | Amber";
          meta.description =
            "Discover the latest blog posts from Amber. Stay updated with our newest articles, guides, and insights.";
          meta.canonical_url_suffix = `${lang ? `/${lang}` : ""}/blog/latest`;
        } else if (isCategory) {
          const categoryName = blogData[0]?.category?.name || slug;
          meta.title = `${categoryName} Blogs and Articles | Amber`;
          meta.description = `Explore blog posts about ${categoryName}. Find helpful articles, guides, and resources.`;
          meta.canonical_url_suffix = `${lang ? `/${lang}` : ""}/blog/category/${slug}`;
        } else if (isTag) {
          const tagName = blogData[0]?.matchedTag?.name || slug;
          meta.title = `${tagName} Blogs and Articles | Amber`;
          meta.description = `Read blog posts tagged with ${tagName}. Discover relevant articles and insights.`;
          meta.canonical_url_suffix = `${lang ? `/${lang}` : ""}/blog/tag/${slug}`;
        } else if (isSearch) {
          meta.title = searchQuery
            ? `Blogs on "${searchQuery}" | Amber`
            : "Search Blog Posts | Amber";
          meta.description = searchQuery
            ? `Search results for "${searchQuery}". Find relevant blog posts and articles.`
            : "Search for blog posts and articles on Amber.";
          meta.canonical_url_suffix = searchQuery
            ? `${lang ? `/${lang}` : ""}/blog/search?query=${encodeURIComponent(searchQuery)}`
            : `${lang ? `/${lang}` : ""}/blog/search`;
          meta.robots = "noindex,follow"; // Make search pages noindex,follow
        }

        meta.keywords = [];

        dispatch(getMetaSuccess(meta));
        dispatch(
          updateBlogData({
            categoryData: blogData,
            categories,
            slug,
            canonical_url: url,
            isCategory,
            isTag,
            isSearch,
            searchQuery,
            totalDocs,
            isLatest,
          }),
        );
      } else {
        // For single blog post, return the first item
        let blogData = res.data?.docs?.[0] || res.data;

        // If blogData is still an object with docs, extract the first one
        if (blogData?.docs && Array.isArray(blogData.docs)) {
          // eslint-disable-next-line prefer-destructuring
          blogData = blogData.docs[0];
        }

        if (!blogData || !blogData.id) {
          console.error("Blog data validation failed:", { blogData, hasId: !!blogData?.id });
          dispatch(updateBlogReadyStatus("failure"));
          if (staticContext) {
            staticContext.statusCode = 404;
          }
          return;
        }

        // Rewrite image URLs for China
        if (blogData.mainImage?.url) {
          blogData.mainImage = {
            ...blogData.mainImage,
            url: getBlogImageUrl(blogData.mainImage.url),
          };
        }

        // Fetch featured blogs for carousel based on blog city/country
        let sidebarFeaturedBlogs: any[] = [];
        try {
          const currentBlogId = blogData.id;
          const cityData = blogData.city;
          const countryRaw = blogData.country || blogData.countryTag;
          // countryTag can be an object { id, name } or just an ID number
          const countryId = typeof countryRaw === "object" ? countryRaw?.id : countryRaw;
          // Skip "Multi-city" sentinel entry
          const isMultiCity =
            typeof cityData === "object" && cityData?.name?.toLowerCase() === "multi-city";
          const rawCityId = typeof cityData === "object" ? cityData?.id : cityData;
          const cityId = isMultiCity ? null : rawCityId;
          const baseSelect = `select[id]=true&select[name]=true&select[slug]=true&select[mainImage]=true&select[category]=true&select[readTime]=true&select[date]=true&select[updatedAt]=true&select[city]=true`;
          const baseFilter = `where[_status][equals]=published&where[isArchived][equals]=false&where[id][not_equals]=${currentBlogId}`;
          const latestFilter = `where[_status][equals]=published&where[isArchived][not_equals]=true`;
          const cacheParam = isDraft ? `&no_cache_param=${Date.now()}` : "";

          const blogPostsUrl = getBlogCollectionUrl("blog-articles");

          // Fetch city, country, and latest blogs in parallel
          const [cityBlogs, countryBlogs, latestBlogs] = await Promise.allSettled([
            cityId
              ? axios.get(
                  `${blogPostsUrl}?where[city][equals]=${cityId}&${baseSelect}&${baseFilter}&limit=10&sort=-updatedAt${cacheParam}`,
                )
              : Promise.resolve(null),
            countryId
              ? axios.get(
                  `${blogPostsUrl}?where[country][equals]=${countryId}&${baseSelect}&${baseFilter}&limit=10&sort=-updatedAt${cacheParam}`,
                )
              : Promise.resolve(null),
            axios.get(
              `${blogPostsUrl}?${baseSelect}&${latestFilter}&limit=10&sort=-updatedAt${cacheParam}`,
            ),
          ]);

          const cityDocs = (cityBlogs.status === "fulfilled" && cityBlogs.value?.data?.docs) || [];
          const countryDocs =
            (countryBlogs.status === "fulfilled" && countryBlogs.value?.data?.docs) || [];
          const latestDocs =
            (latestBlogs.status === "fulfilled" && latestBlogs.value?.data?.docs) || [];

          // Merge with priority: city > country (excl. other cities) > latest, deduplicating by ID
          const seen = new Set<number>([currentBlogId]);

          cityDocs.forEach((blog: any) => {
            if (!seen.has(blog.id) && sidebarFeaturedBlogs.length < 10) {
              seen.add(blog.id);
              sidebarFeaturedBlogs.push(blog);
            }
          });

          // From country docs, only include blogs with no city, multi-city (ID 1), or same city
          countryDocs.forEach((blog: any) => {
            if (!seen.has(blog.id) && sidebarFeaturedBlogs.length < 10) {
              const blogCityId = typeof blog.city === "object" ? blog.city?.id : blog.city;
              const isMultiCityBlog = blogCityId === 1;
              const isNoCityBlog = !blogCityId;
              const isSameCityBlog = blogCityId === cityId;
              if (isNoCityBlog || isMultiCityBlog || isSameCityBlog) {
                seen.add(blog.id);
                sidebarFeaturedBlogs.push(blog);
              }
            }
          });

          latestDocs.forEach((blog: any) => {
            if (!seen.has(blog.id) && sidebarFeaturedBlogs.length < 10) {
              seen.add(blog.id);
              sidebarFeaturedBlogs.push(blog);
            }
          });

          sidebarFeaturedBlogs = sidebarFeaturedBlogs.map((blog: any) => ({
            ...blog,
            mainImage: blog.mainImage
              ? { ...blog.mainImage, url: getBlogImageUrl(blog.mainImage.url) }
              : blog.mainImage,
          }));
        } catch {
          // Silently fail — featured blogs carousel is non-critical
          sidebarFeaturedBlogs = [];
        }

        // Fetch blog partners for sidebar carousel
        let blogPartners: any[] = [];
        if (!config.IS_CHINA_APP) {
          try {
            let blogPartnersUrl = `${config.CMS_API_URL}/api/blog-partners`;
            if (isDraft) {
              blogPartnersUrl += `?no_cache_param=${Date.now()}`;
            }
            const blogPartnersResponse = await axios.get(blogPartnersUrl);
            blogPartners = blogPartnersResponse.data?.docs?.[0]?.partners || [];
          } catch {
            blogPartners = [];
          }
        }

        // meta updates
        const meta: any = {};
        meta.title = blogData?.metaTitle || "";
        meta.description = blogData?.metaDescription || "";
        meta.keywords = blogData?.metaKeywords?.split(",") || [];
        meta.canonical_url_suffix = `${lang ? `/${lang}` : ""}/blog/${blogData?.slug}`;
        meta.ogImage = getImagePath(getBlogImageUrl(blogData?.mainImage?.url || ""), "medium");
        meta.excludeAlternateLinks = true; // Blog pages don't need CN alternate links
        // Format date as MMM DD, YYYY
        meta.dateModified = blogData.updatedAt
          ? new Date(blogData.updatedAt).toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
            })
          : "";
        // For draft blog posts, set noindex
        if (isDraft) {
          meta.index = false;
        }
        let videoSchemaData: any[] = [];
        let faqSchemaData: any[] = [];
        let howToSchemaData: any[] = [];
        let itemListSchemaData: any[] = [];
        let blogPostSchemaData: any = {};
        try {
          blogPostSchemaData = getBlogPostSchema(blogData);
          if (blogData?.isVideoObjectSchema === "yes") {
            videoSchemaData = getBlogVideoSchema(blogData);
          }
          if (blogData?.isFaqSchema === "yes") {
            faqSchemaData = getBlogFAQSchema(blogData);
          }
          if (blogData?.isHowToSchema === "yes") {
            howToSchemaData = getBlogHowToSchema(blogData);
          }
          if (blogData?.isItemListSchema === "yes") {
            itemListSchemaData = getBlogItemListSchema(blogData);
          }
        } catch {
          // Schema generation is non-critical — don't crash the page
        }
        const schemaData = [
          blogPostSchemaData,
          videoSchemaData,
          faqSchemaData,
          howToSchemaData,
          itemListSchemaData,
        ].flat();

        meta.schemaData = schemaData;

        dispatch(getMetaSuccess(meta));
        dispatch(
          updateBlogData({
            data: blogData,
            sidebarFeaturedBlogs,
            blogPartners,
            slug,
            canonical_url: url,
            isCategory: false,
          }),
        );
      }

      dispatch(updateBlogReadyStatus("success"));
    } catch (error) {
      console.error("Error fetching blog data:", error);
      dispatch(updateBlogReadyStatus("failure"));
      if (staticContext) {
        staticContext.statusCode = 500;
      }
    }
  };

export default getBlogPageData;
